import { z } from "zod";
import { useCallback, useMemo } from "react";
import { useDebouncedRequest } from "hooks/useDebouncedRequest";
import { useTenant } from "hooks/settings";
import {
  AutoRecipeDefaultsRequest,
  AutoRecipeParameterDefinition,
  AutoRecipeParameterScope,
  useValidateAutoRecipeDefaultsMutation,
} from "store/api/generatedApi";
import { createParameterValidator } from "./parameterSchema";

const rejectionSchema = z.object({
  status: z.union([z.literal(409), z.literal(422)]),
  data: z.unknown(),
});

export type AutoRecipeValidationResponse =
  | { status: "accepted" }
  | {
      status: "rejected";
      reason: "parameters" | "stale";
      message: string | null;
    };

export const useAutoRecipeValidation = (
  candidate: AutoRecipeDefaultsRequest,
  definition: AutoRecipeParameterDefinition,
  scope: AutoRecipeParameterScope
) => {
  const tenantName = useTenant();
  const [validate] = useValidateAutoRecipeDefaultsMutation();
  const validateParameters = useMemo(
    () => createParameterValidator(definition),
    [definition]
  );
  const schemaValidation = useMemo(
    () => validateParameters(candidate.parameters),
    [validateParameters, candidate.parameters]
  );
  const { valid: isValid } = schemaValidation;
  const request = useMemo(
    () => (isValid ? { tenantName, candidate, scope } : null),
    [isValid, tenantName, candidate, scope]
  );
  const check = useCallback(
    (request: {
      tenantName: string;
      candidate: AutoRecipeDefaultsRequest;
      scope: AutoRecipeParameterScope;
    }) => {
      const pending = validate({
        tenantName: request.tenantName,
        scope: request.scope,
        autoRecipeDefaultsRequest: request.candidate,
      });
      return {
        response: pending.unwrap().then(
          (): AutoRecipeValidationResponse => ({ status: "accepted" }),
          (error: unknown): AutoRecipeValidationResponse => {
            const rejected = rejectionSchema.safeParse(error);
            if (!rejected.success) throw error;
            const message = z
              .object({ detail: z.string() })
              .safeParse(rejected.data.data);
            return {
              status: "rejected",
              reason: rejected.data.status === 409 ? "stale" : "parameters",
              message: message.success ? message.data.detail : null,
            };
          }
        ),
        abort: () => {
          pending.abort();
        },
      };
    },
    [validate]
  );
  const pending = useDebouncedRequest({ request, check, delay: 300 });
  const rejection =
    pending.isCurrent &&
    pending.completion?.status === "success" &&
    pending.completion.response.status === "rejected"
      ? pending.completion.response
      : null;
  return {
    ...pending,
    schemaValidation,
    rejection,
    isValid: isValid && rejection === null,
  };
};
